/**
 * AnalyticsConsentBanner — the cookie/analytics choice for UK/EU visitors
 * (2026-10-08; the founder advertises there).
 *
 * Shown only when the device time zone is in the UK/EU (lib/analyticsConsent
 * has the heuristic) and no choice is stored — or anywhere, after the Privacy
 * page's "Cookie settings" re-opens it. Never inside an iframe (embeds,
 * previews). Until the visitor chooses, PostHog runs without cookies,
 * autocapture or replay (lib/posthog.ts) and the Meta Pixel doesn't load
 * (lib/metaPixel.ts); either button closes the banner for
 * good, so it never sits over the page's own buttons once answered.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/button";
import {
  CONSENT_REOPEN_EVENT,
  deviceTimeZone,
  isConsentRegion,
  isEmbedded,
  readConsent,
  shouldShowConsentBanner,
} from "@/lib/analyticsConsent";
import { denyAnalyticsConsent, grantAnalyticsConsent } from "@/lib/posthog";

export default function AnalyticsConsentBanner() {
  const [visible, setVisible] = useState(() =>
    shouldShowConsentBanner({
      choice: readConsent(),
      consentRegion: isConsentRegion(deviceTimeZone()),
      embedded: isEmbedded(),
    }),
  );

  useEffect(() => {
    const reopen = () => {
      if (!isEmbedded()) setVisible(true);
    };
    window.addEventListener(CONSENT_REOPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, reopen);
  }, []);

  if (!visible) return null;

  // The Meta Pixel only runs on the website (lib/metaPixel.ts), so the apps
  // don't mention it (review 2026-10-09).
  let native = false;
  try { native = Capacitor.isNativePlatform(); } catch { /* web */ }

  const accept = () => {
    grantAnalyticsConsent();
    setVisible(false);
  };
  const decline = () => {
    denyAnalyticsConsent();
    setVisible(false);
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] pointer-events-none"
      data-testid="analytics-consent-banner"
    >
      <div
        role="region"
        aria-label="Cookie choice"
        className="pointer-events-auto mx-auto max-w-md rounded-2xl border border-border bg-background/95 p-4 shadow-xl backdrop-blur"
      >
        <p className="text-sm leading-snug text-foreground">
          {native
            ? "We use analytics cookies to understand how TrybeUP is used and to improve it."
            : "We use cookies to understand how TrybeUP is used and to measure our ads (PostHog and Meta)."}{" "}
          <Link to="/privacy" className="text-primary underline underline-offset-2">
            Privacy
          </Link>
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button type="button" variant="outline" onClick={decline} data-testid="analytics-consent-decline">
            Decline
          </Button>
          <Button type="button" onClick={accept} data-testid="analytics-consent-accept">
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
